import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useNgStore } from "@/features/ng-filter/stores";
import { useCurrentBoardSlug } from "@/shared/hooks/useCurrentBoardSlug";
import { formatDate } from "@/shared/lib";
import { useSearch } from "../../hooks/useSearch";

interface Props {
  query: string;
}

/** 検索 hit を NG リストに突合する用の HTML タグ除去 (簡易) */
const stripHtml = (s: string): string => s.replace(/<[^>]+>/g, "");

export const SearchResults: React.FunctionComponent<Props> = ({
  query,
}: Props) => {
  const { data, isLoading, error, isFetching } = useSearch(query);
  const boardSlug = useCurrentBoardSlug();
  const {
    enabled: ngEnabled,
    showNgContent,
    hiddenThreadIds,
    ngTitles,
    ngWords,
    ngRegexes,
  } = useNgStore();

  const visibleHits = useMemo(() => {
    if (!data) return [];
    if (!ngEnabled || showNgContent) return data;
    const hiddenSet = new Set(hiddenThreadIds);
    const compiledRegexes = ngRegexes
      .map((p) => {
        try {
          return new RegExp(p);
        } catch {
          return null;
        }
      })
      .filter((r): r is RegExp => r !== null);
    return data.filter((hit) => {
      if (hiddenSet.has(hit.threadId)) return false;
      const title = hit.title ?? "";
      const body = hit.bodySample ? stripHtml(hit.bodySample) : "";
      const haystack = `${title}\n${body}`;
      if (ngTitles.some((t) => title.includes(t))) return false;
      if (ngWords.some((w) => haystack.includes(w))) return false;
      if (compiledRegexes.some((r) => r.test(haystack))) return false;
      return true;
    });
  }, [
    data,
    ngEnabled,
    showNgContent,
    hiddenThreadIds,
    ngTitles,
    ngWords,
    ngRegexes,
  ]);

  if (isLoading || (isFetching && !data)) {
    return (
      <div className="p-8 text-center text-muted-foreground text-sm">
        検索中…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center text-destructive text-sm">
        検索に失敗しました
      </div>
    );
  }

  if (!data || data.length === 0 || visibleHits.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground text-sm">
        「{query}」に一致するレスが見つかりませんでした
      </div>
    );
  }

  return (
    <div className="p-2 pb-20 space-y-2">
      <div className="px-2 py-1 text-xs text-muted-foreground">
        「{query}」の検索結果: {visibleHits.length}件 (post 単位)
      </div>
      {visibleHits.map((hit) => (
        <Link
          key={hit.postId}
          to="/$boardSlug/thread/$threadId"
          params={{ boardSlug, threadId: hit.threadId }}
          hash={`post-${hit.postId}`}
          className="block rounded-lg border border-border bg-card p-3 hover:bg-muted transition-colors"
        >
          <div className="text-sm font-medium text-foreground truncate">
            {hit.title ?? "(無題)"}
          </div>
          {hit.bodySample && (
            <div className="mt-1 text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap break-words">
              {hit.bodySample}
            </div>
          )}
          <div className="mt-1 text-[10px] text-muted-foreground">
            {formatDate(hit.createdAt)}
          </div>
        </Link>
      ))}
    </div>
  );
};
